import { runInAction, action, observable, computed } from "mobx";
import { IStoreLifeCycle } from "@interface";

import { LoginErrorResponseModel } from "@data-types";
import { UserAccount } from "@data-types/user";

import {
	LoginHistoryResponseModel,
	ActivationTypeEnum as AccountActivationTypeEnum,
	LoginResponseDto,
} from "@api-types/user/LoginResponseDto";
import {
	LoginRequestDto,
	LoginSSORequestDto,
} from "@api-types/user/LoginRequestDto";

import { getCurrentCulture } from "@utils";
import { LazyImportWithLoadFailHandle as lazy } from "@lib/lazy-import-with-guard/LazyImportWithGuard";
import { LoginType } from "@utils/common/loginTypeEnum";
import { MitAuthDto } from "@api-types/integrations/MitAuthDto";
import { StorageStateKeysEnum } from "@utils/storage";
import {
	getRedirectOriginUrl,
	isCapacitorPlatform,
} from "@utils/specific/capacitor";
import { LoaderStore } from "@state/stores/common";
import { BankIdAuthDto } from "@api-types/integrations";
import BlockUserActionService from "@services/user/BlockUserActionService";
import { UseFormReturn } from "react-hook-form";
import { LoginHash } from "@api-types/integrations/MitIdWidgetDto";
import { LoginErrorCodes } from "@data-types/user/LoginErrorCodes";
import { RequiredUserAction, UserData } from "./types";
import { analyticsTracker } from "@utils/analytics";
import { User, UserAuthStore, UserToken } from "@state/shared";

const loadFailPath = `/${getCurrentCulture()}/app-update`;

// prettier-ignore
const PersonalDataService = lazy(loadFailPath, ()=>import("@services/account-settings/PersonalDataService"));
// prettier-ignore
const UserService = lazy(loadFailPath, ()=>import("@services/user/UserService"))

class LoginStore implements IStoreLifeCycle {
	userStore: UserAuthStore;
	formContext?: UseFormReturn<LoginRequestDto>;

	@observable isLoginWorking: boolean = false;
	@observable isStoreInitialized: boolean = false;
	@observable loginError: LoginErrorResponseModel | null = null;
	@observable userMatchingDataList: string[] = [];
	@observable isUserMatchingInProgress: boolean = false;
	@observable isDataConfirmationInProgress: boolean = false;
	@observable loader: LoaderStore;
	@observable shouldShowPageRefreshPopup: boolean = false;

	/**
	 * In case of sso login (redirect with params) language service triggers rerender:
	 * - resulting in useEffects triggering twice
	 * flag is set to true on first render, value is checked on entering use effect to make sure we trigger login only once
	 */
	isSSOLoginInitialized: boolean = false;

	@computed public get isLoading() {
		return this.loader.isLoading;
	}

	@computed public get nextRequiredUserAction(): RequiredUserAction {
		if (
			this.isLoginWorking &&
			!BlockUserActionService.checkIfActionBlocked(
				RequiredUserAction.IsLoginWorking
			)
		) {
			return RequiredUserAction.IsLoginWorking;
		}

		if (
			this.userStore.accountActivation?.activationType ===
				AccountActivationTypeEnum.AccountActivation &&
			!BlockUserActionService.checkIfActionBlocked(
				RequiredUserAction.AccountActivation
			)
		) {
			return RequiredUserAction.AccountActivation;
		}

		if (
			this.userStore.accountActivationStore
				.isAccountActivationCongratulationsActive &&
			!BlockUserActionService.checkIfActionBlocked(
				RequiredUserAction.AccActivationCongratulation
			)
		) {
			return RequiredUserAction.AccActivationCongratulation;
		}

		if (
			this.isDataConfirmationInProgress &&
			!BlockUserActionService.checkIfActionBlocked(
				RequiredUserAction.ConfirmUserData
			)
		) {
			return RequiredUserAction.ConfirmUserData;
		}

		if (
			this.userStore.accountActivation?.activationType ===
				AccountActivationTypeEnum.PasswordChange &&
			!BlockUserActionService.checkIfActionBlocked(
				RequiredUserAction.PasswordChange
			)
		) {
			return RequiredUserAction.PasswordChange;
		}

		if (
			this.isUserMatchingInProgress &&
			!BlockUserActionService.checkIfActionBlocked(
				RequiredUserAction.UserMatching
			)
		) {
			return RequiredUserAction.UserMatching;
		}

		if (
			this.userStore.user?.bettingActivityNeeded &&
			(this.userStore.bettingActivityStore.bettingActivityFetchedData
				?.bettingActivity != null ||
				this.userStore.bettingActivityStore.bettingActivityFetchedData
					?.userActivity != null) &&
			!BlockUserActionService.checkIfActionBlocked(
				RequiredUserAction.BettingActivity
			)
		) {
			return RequiredUserAction.BettingActivity;
		}

		if (
			this.userStore.user?.bettingActivityNeeded &&
			!BlockUserActionService.checkIfActionBlocked(
				RequiredUserAction.BettingActivity
			)
		) {
			return RequiredUserAction.IsLoginWorking;
		}

		if (
			this.userStore.user?.shouldAcceptTermsAndConditions &&
			!BlockUserActionService.checkIfActionBlocked(
				RequiredUserAction.AcceptNewTerms
			)
		) {
			return RequiredUserAction.AcceptNewTerms;
		}

		if (this.userStore.isPartiallyLoggedIn && !this.userStore.isLoggedIn) {
			return RequiredUserAction.IsLoginWorking;
		}

		if (
			this.userStore.user?.shouldAcceptGdpr &&
			!isCapacitorPlatform() &&
			!BlockUserActionService.checkIfActionBlocked(
				RequiredUserAction.AcceptGdpr
			)
		) {
			//currently disabled for capacitor platform
			return RequiredUserAction.AcceptGdpr;
		}

		return RequiredUserAction.None;
	}

	get pathname() {
		return window.location.pathname[0] == "/"
			? window.location.pathname.slice(1)
			: window.location.pathname;
	}

	get redirectUrl() {
		return getRedirectOriginUrl(this.pathname, true);
	}

	//#region constructor
	constructor(userStore: UserAuthStore) {
		this.userStore = userStore;
		this.loader = new LoaderStore();
	}

	@action.bound
	handleSsoLogin(
		params: Record<string, string>,
		formContext?: UseFormReturn<LoginRequestDto>
	) {
		this.formContext = formContext;

		if (this.isSSOLoginInitialized) {
			return;
		}

		const { sessionId, code, token, error } = params;

		if (error) {
			this.userStore.rootStore.localStorageProvider.remove(
				StorageStateKeysEnum.MITID_LOGIN_HASH
			);
			return;
		}

		// sso auth
		if (token) {
			this.isSSOLoginInitialized = true;

			const data: LoginSSORequestDto = { token };
			this.handleLoginSubmit(data, LoginType.SSO);
			return;
		}

		// bank auth. ignore 'sessionId' param if BankID integration is disabled
		if (sessionId && WEBPACK_IS_BANKID_ENABLED) {
			this.isSSOLoginInitialized = true;

			const data: BankIdAuthDto = { sessionId };
			this.handleLoginSubmit(data, LoginType.BANKID);
			return;
		}

		// mit auth
		if (code) {
			this.isSSOLoginInitialized = true;

			const { scope, state, iss } = params;

			const loginHash = JSON.parse(
				this.userStore.rootStore.localStorageProvider.get(
					StorageStateKeysEnum.MITID_LOGIN_HASH
				) as string
			) as LoginHash;
			if (loginHash?.state == null || loginHash?.nonce == null) {
				this.loginError = {
					errorCode: "5555",
					message: "MIT_MISSING_ORIGINAL_STATE",
				};
				this.checkLoginErrors();
				return;
			}

			const mitData: MitAuthDto = {
				code,
				scope,
				state,
				iss,
				originalState: loginHash.state,
				originalNonce: loginHash.nonce,
				redirectUrl: this.redirectUrl,
			};

			this.handleLoginSubmit(mitData, LoginType.MITID);
		}
	}

	@action.bound
	checkLoginErrors() {
		if (
			this.loginError != null &&
			typeof this.loginError.userData !== "string" &&
			this.loginError.userData?.activationToken == null
		) {
			this.loader.resume();
			this.formContext && this.formContext.resetField("password");
		}
	}

	@action.bound
	async handleLoginSubmit(
		data: LoginRequestDto | LoginSSORequestDto | BankIdAuthDto | MitAuthDto,
		loginType: LoginType = LoginType.DEFAULT
	) {
		this.loader.suspend();
		this.resetLoginErrorState();

		if (loginType == LoginType.MITID) {
			this.userStore.rootStore.localStorageProvider.remove(
				StorageStateKeysEnum.MITID_LOGIN_HASH
			);
		}

		await this.onLoginSubmit(data, loginType);
	}

	@action.bound
	public onInitialize(): void {
		this.readFromLocalStorage(null);
		window.addEventListener("storage", this.storageActionListener);
	}

	@action.bound
	public onInitializeLogin(): void {
		this.userStore.removeUserFromLocalStorage();

		this.userStore.rootStore.automaticLogoutStore.clearTimer();
		this.userStore.rootStore.realityCheckStore.clearTimer();
		this.userStore.rootStore.closePopups();

		//remove authorization cookies

		document.cookie = `authorization=;expires=${new Date().toUTCString()};path=/`;
		document.cookie = `authorization_token=;expires=${new Date().toUTCString()};path=/`;

		this.isStoreInitialized = true;
	}

	@action.bound
	public async onLoginSubmit(
		loginData:
			| LoginRequestDto
			| LoginSSORequestDto
			| MitAuthDto
			| BankIdAuthDto,
		loginType: LoginType = LoginType.DEFAULT
	): Promise<void> {
		this.isLoginWorking = true;
		try {
			const response = await (
				await UserService
			).default.loginUser(loginData, loginType);

			if (response != null) {
				const sln = (loginData as LoginRequestDto).stayLoggedIn;
				if (sln) {
					this.userStore.rootStore.localStorageProvider.set(
						StorageStateKeysEnum.SLN,
						`${sln}`
					);
				}
				this.userStore.login(response);
				analyticsTracker.trackLoginSuccess(response.primary.user);
				if (
					Number(response.errorCode) ===
					LoginErrorCodes.dataConfirmationRequiredCode
				) {
					this.setUserDataConfirmationState(true);
					await this.resolveUserDataConfirmationData(response);
				}

				await this.userStore.userMatchingStore.resolveUserMatchingData(
					Number(response.errorCode)
				);
			}
		} catch (error) {
			if (error.errorCode === 400259285) {
				this.shouldShowPageRefreshPopup = true;
				return;
			}

			if (error.errorCode == 400272) {
				this.loginError = {
					errorCode: error.errorCode,
					message: "MIT_UNSUPPORTED_GRANT_TYPE", //same error message for bankId
				};
				return;
			}

			if (error?.userData) {
				if (error.userData?.activationToken) {
					//show page with user data
					this.userStore.userDataConfirmationStore.setUserDataToBeConfirmed(
						{
							...error.userData,
							isInternetUser: false,
						}
					);
					this.setUserDataConfirmationState(true);
					this.userStore.accountActivationStore.setUserConfirmationData(
						error.userData
					);
					this.resetLoginErrorState();
				} else {
					//show message about shop
					runInAction(() => {
						this.loginError = {
							errorCode: error.errorCode,
							message: error.userData.shopInfo,
						};
					});
				}
			} else {
				runInAction(() => (this.loginError = error));
			}
		} finally {
			runInAction(() => {
				this.isLoginWorking = false;
			});
			this.checkLoginErrors();
		}
	}

	@action.bound
	resetLoginErrorState(resetLoader?: boolean) {
		this.loginError = null;
		resetLoader && this.loader.resume();
	}

	@action.bound
	public setUserDataConfirmationState(flag: boolean): void {
		if (!flag) {
			this.userStore.rootStore.localStorageProvider.remove(
				StorageStateKeysEnum.UDC
			);
		} else {
			this.userStore.rootStore.localStorageProvider.set(
				StorageStateKeysEnum.UDC,
				`${flag}`
			);
		}

		this.isDataConfirmationInProgress = flag;
	}

	@action.bound
	public async resolveUserDataConfirmationData(
		response: LoginResponseDto
	): Promise<void> {
		const fetchedUserData = await (
			await PersonalDataService
		).default.getPersonalData(false);
		if (fetchedUserData != null && fetchedUserData.data != null) {
			const userDataModel = fetchedUserData.data;
			const userData: UserData = {
				activationToken:
					response.primary.accountActivation?.activationToken,
				agencyIdentifier: response.primary.user.agencyId,
				birthPlace: userDataModel.birthPlace,
				city: userDataModel.city,
				countryId: userDataModel.countryId!,
				firstName: userDataModel.firstName,
				houseNumber: userDataModel.houseNumber,
				lastName: userDataModel.lastName,
				shopId: response.primary.user.shopId,
				shopInfo: null,
				street: userDataModel.street,
				userName: userDataModel.userName,
				zipCode: userDataModel.zipCode,
				isInternetUser: response.primary.user.isInternetUser,
				currentEmail: "",
				dob: userDataModel.dob,
				language: userDataModel.language,
				mobilePhone: userDataModel.mobilePhone,
				payPin: userDataModel.payPin,
				receiveNewsletter: userDataModel.receiveNewsletter,
				telephoneNumber: userDataModel.telephoneNumber,
				titleId: userDataModel.titleId,
				occupation: userDataModel.occupation,
				nationality: userDataModel.nationality,
				birthName: userDataModel.birthName,
				documentExpiration: userDataModel.documentExpiration,
			};
			this.userStore.userDataConfirmationStore.setUserDataToBeConfirmed(
				userData
			);
		}
	}

	@action.bound
	async resolveUserActivationLogin(
		primary: {
			user: User;
			token: UserToken;
			userAccounts: UserAccount[];
			loginHistory: LoginHistoryResponseModel;
		},
		secondary?: {
			user: User;
			token: UserToken;
		},
		errorCode?: number
	): Promise<void> {
		this.isLoginWorking = true;
		this.userStore.login({
			primary,
			secondary,
		});

		if (errorCode === LoginErrorCodes.dataConfirmationRequiredCode) {
			this.setUserDataConfirmationState(true);
			await this.resolveUserDataConfirmationData({
				//@ts-expect-error
				primary: primary,
				//@ts-expect-error
				secondary: secondary,
			});
		}

		await this.userStore.userMatchingStore.resolveUserMatchingData(
			errorCode
		);

		runInAction(() => {
			this.isLoginWorking = false;
		});
	}

	@action.bound
	private storageActionListener = (e: StorageEvent) => {
		this.readFromLocalStorage(e.key);
	};

	@action.bound
	private readFromLocalStorage(key: string | null) {
		if (key == null || key == "user_matching") {
			this.userStore.userMatchingStore.setUserMatchingState(
				Boolean(
					this.userStore.rootStore.localStorageProvider.get(
						StorageStateKeysEnum.USER_MATCHING
					)
				)
			);
		}

		if (key == null || key == "udc") {
			this.setUserDataConfirmationState(
				Boolean(
					this.userStore.rootStore.localStorageProvider.get(
						StorageStateKeysEnum.UDC
					)
				)
			);
		}
		if (key == null || key == "user-data-to-be-confirmed") {
			this.userStore.userDataConfirmationStore.setUserDataToBeConfirmed(
				JSON.parse(
					this.userStore.rootStore.localStorageProvider.get(
						StorageStateKeysEnum.USER_DATA_TO_BE_CONFIRMED
					) as string
				)
			);
		}
	}

	@action.bound
	public onDispose(): void {
		window.removeEventListener("storage", this.storageActionListener);
		this.userStore.userDataConfirmationStore.onDisposeUserDataConfirmation();
		this.userStore.accountActivationStore.onDisposeAccountActivation();
		this.isStoreInitialized = false;
		this.isSSOLoginInitialized = false;
	}
}

export default LoginStore;
